/* Sprint 2R-2C — Admin Unified Search modal/popover.
 *
 * Triggered from a global search-bar visible in every admin tab. Searches
 * shipments, cages, routes, drivers, customers in a single call.
 */
import React from "react";
import { adminApi } from "../../lib/api";
import { Search, Package, Boxes, Map, Truck, Building2, X, Loader2 } from "lucide-react";

export default function AdminSearchBar() {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [results, setResults] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await adminApi().get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && e.target?.tagName !== "INPUT" && e.target?.tagName !== "TEXTAREA") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => document.getElementById("admin-search-input")?.focus(), 0);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <button
        onClick={() => { setOpen(true); setTimeout(() => document.getElementById("admin-search-input")?.focus(), 0); }}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm"
        data-testid="admin-search-trigger"
      >
        <Search size={14}/> Søg... <span className="text-xs text-brand-text2 ml-2 hidden md:inline">(/)</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-20" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-2xl mx-4 border border-brand shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <Search size={20} className="text-brand-text2"/>
              <input
                id="admin-search-input"
                type="text"
                placeholder="Søg pakke / bur / rute / chauffør / kunde / postnummer..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="flex-1 outline-none text-base"
                data-testid="admin-search-input"
              />
              {loading && <Loader2 className="animate-spin text-brand-text2" size={16}/>}
              <button onClick={() => setOpen(false)} data-testid="admin-search-close">
                <X size={18} className="text-brand-text2"/>
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-4 space-y-4" data-testid="admin-search-results">
              {!results && q.length < 2 && (
                <div className="text-sm text-brand-text2">Indtast mindst 2 tegn (tracking-nummer, bur-nummer, rute, chauffør-navn, postnr...)</div>
              )}
              {results && results.total_hits === 0 && (
                <div className="text-sm text-brand-text2">Ingen resultater for &quot;{results.query}&quot;</div>
              )}
              {results && results.total_hits > 0 && (
                <>
                  <ResultGroup icon={Package} label="Forsendelser" items={results.results.shipments}
                               render={(p) => (
                                 <div>
                                   <div className="font-mono text-sm font-bold">{p.shipment_number}</div>
                                   <div className="text-xs text-brand-text2">{p.status} · {p.delivery_postal_code} {p.delivery_city} · {p.recipient_name}</div>
                                 </div>
                               )}/>
                  <ResultGroup icon={Boxes} label="Bure" items={results.results.cages}
                               render={(c) => (
                                 <div>
                                   <div className="font-mono text-sm font-bold">{c.cage_number}</div>
                                   <div className="text-xs text-brand-text2">{c.status} · rute {c.route_number} · {c.scanned_count}/{c.expected_count} pakker</div>
                                 </div>
                               )}/>
                  <ResultGroup icon={Map} label="Ruter" items={results.results.routes}
                               render={(r) => (
                                 <div>
                                   <div className="font-mono text-sm font-bold">{r.route_number || r.id.slice(0,8)}</div>
                                   <div className="text-xs text-brand-text2">{r.status} · {r.lifecycle_status} · {r.route_type}</div>
                                 </div>
                               )}/>
                  <ResultGroup icon={Truck} label="Chauffører" items={results.results.drivers}
                               render={(d) => (
                                 <div>
                                   <div className="text-sm font-bold">{d.name}</div>
                                   <div className="text-xs text-brand-text2">{d.email} {d.phone && `· ${d.phone}`}</div>
                                 </div>
                               )}/>
                  <ResultGroup icon={Building2} label="Kunder" items={results.results.customers}
                               render={(c) => (
                                 <div>
                                   <div className="text-sm font-bold">{c.company_name}</div>
                                   <div className="text-xs text-brand-text2">CVR {c.cvr} · {c.tier || "—"} · {c.billing_email}</div>
                                 </div>
                               )}/>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResultGroup({ icon: Icon, label, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs uppercase text-brand-text2 mb-2 flex items-center gap-2">
        <Icon size={12}/> {label} ({items.length})
      </div>
      <div className="space-y-1">
        {items.map((it, idx) => (
          <div key={it.id || idx} className="p-2 border border-gray-100 hover:bg-brand-bg2"
               data-testid={`search-result-${it.id}`}>
            {render(it)}
          </div>
        ))}
      </div>
    </div>
  );
}
